import React from "react";
import { Box, Button, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { BarChart } from "@mui/x-charts/BarChart";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { VoicesTopPost, VoicesTotals, VoicesWeek } from "../../../Models/voicesResults";
import { formatPostTime } from "../Posts/VoicesPostRows";

const number = (value: number) => value.toLocaleString();

export const VoicesResultTiles: React.FC<{ totals: VoicesTotals }> = ({ totals }) => {
  const { t } = useTranslation("voicesPosts");
  const tiles: [string, string][] = [
    [t("results_posts"), number(totals.posts)],
    [t("results_impressions"), number(totals.impressions)],
    [t("results_interactions"), number(totals.interactions)],
    [t("results_engagement"), `${totals.engagement_rate.toLocaleString()} %`],
    [t("results_reactions"), number(totals.reactions)],
    [t("results_comments"), number(totals.comments)],
    [t("results_clicks"), number(totals.clicks)],
    [t("results_reposts"), number(totals.reposts)],
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
        gap: 1.5,
      }}
    >
      {tiles.map(([label, value], index) => (
        <Box
          key={label}
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
            backgroundColor: index < 4 ? color.orange50 : color.white,
          }}
        >
          <Typography variant="caption">{label}</Typography>
          <Typography sx={{ fontSize: "1.35rem", fontWeight: 700, color: color.gray900 }}>
            {value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const VoicesWeeklyChart: React.FC<{ weeks: VoicesWeek[] }> = ({ weeks }) => {
  const { t, i18n } = useTranslation("voicesPosts");
  if (!weeks.length) return null;
  const labels = weeks.map((week) =>
    new Date(`${week.week}T00:00:00`).toLocaleDateString(i18n.language, {
      day: "numeric",
      month: "short",
    }),
  );

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {t("results_weekly")}
      </Typography>
      <BarChart
        height={260}
        xAxis={[{ scaleType: "band", data: labels }]}
        yAxis={[{ id: "impressions" }, { id: "interactions" }]}
        rightAxis="interactions"
        series={[
          {
            data: weeks.map((week) => week.impressions),
            label: t("results_impressions"),
            color: color.orange500,
            yAxisKey: "impressions",
          },
          {
            data: weeks.map((week) => week.interactions),
            label: t("results_interactions"),
            color: color.gray700,
            yAxisKey: "interactions",
          },
        ]}
        slotProps={{ legend: { labelStyle: { fill: color.gray700, fontSize: 12 } } }}
        sx={{
          "& .MuiChartsAxis-tickLabel": { fill: `${color.gray600} !important` },
          "& .MuiChartsAxis-line, & .MuiChartsAxis-tick": { stroke: `${color.gray300} !important` },
        }}
      />
    </Box>
  );
};

export const VoicesTopPosts: React.FC<{ posts: VoicesTopPost[]; title: string }> = ({
  posts,
  title,
}) => {
  const { t } = useTranslation("voicesPosts");
  if (!posts.length) return null;

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {posts.map((post) => (
        <Box
          key={post.id}
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            mt: 1,
            p: 1.25,
            borderRadius: 2,
            border: `1px solid ${color.gray200}`,
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
            >
              {post.preview || "…"}
            </Typography>
            <Typography variant="caption">
              {[post.target.name, formatPostTime(post.published_at), post.campaign_name]
                .filter(Boolean)
                .join(" · ")}
            </Typography>
          </Box>
          <Typography variant="caption" sx={{ whiteSpace: "nowrap", textAlign: "right" }}>
            {t("results_postFigures", {
              impressions: number(post.impressions),
              interactions: number(post.interactions),
              rate: post.engagement_rate.toLocaleString(),
            })}
          </Typography>
          {post.linkedin_url && (
            <Button
              size="small"
              endIcon={<OpenInNewIcon fontSize="small" />}
              onClick={() => window.open(post.linkedin_url as string, "_blank", "noopener,noreferrer")}
              sx={{ textTransform: "none", color: color.orange600, whiteSpace: "nowrap" }}
            >
              LinkedIn
            </Button>
          )}
        </Box>
      ))}
    </Box>
  );
};
